import { alpha, Backdrop, Fade, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { Loader } from '@/components/progress/Loader/Loader';
import { PRIMITIVES } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

const INITIAL_STATUS_LINES = [
  'Fetching tile details',
  'Generating relevant context',
  'Crafting personalized notes',
];

const STATUS_LINE_DELAY_MS = 2500;

interface TileNotesLoadingOverlayProps {
  open: boolean;
  currentTileNote?: string | null;
}

export function TileNotesLoadingOverlay({
  open,
  currentTileNote,
}: TileNotesLoadingOverlayProps) {
  const [visibleLines, setVisibleLines] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setVisibleLines([]);
      return;
    }

    if (currentTileNote) {
      setVisibleLines([]);
      return;
    }

    setVisibleLines([INITIAL_STATUS_LINES[0]!]);

    const timers: ReturnType<typeof setTimeout>[] = [];

    INITIAL_STATUS_LINES.slice(1).forEach((line, index) => {
      const timer = setTimeout(
        () => {
          setVisibleLines((prev) => [...prev, line]);
        },
        STATUS_LINE_DELAY_MS * (index + 1)
      );
      timers.push(timer);
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [open, currentTileNote]);

  const hasStreamingNote = Boolean(currentTileNote);
  const displayLines = hasStreamingNote ? [currentTileNote!] : visibleLines;

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: zIndexes.HIGHLIGHT,
        position: 'absolute',
        backgroundColor: alpha(PRIMITIVES.WHITE, 0.88),
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Loader circularProgressProps={{ size: 40 }} />
      <Stack spacing={0.5} alignItems="center">
        <Typography variant="title-s">Generating notes</Typography>
        <Stack
          spacing={0.25}
          alignItems="center"
          sx={{ minHeight: 60, justifyContent: 'flex-start' }}
        >
          {displayLines.map((line, index) => (
            <Fade key={`${line}-${index}`} in timeout={400}>
              <Typography
                variant="body-xs"
                sx={{
                  textAlign: 'center',
                  maxWidth: 400,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {line}
              </Typography>
            </Fade>
          ))}
        </Stack>
      </Stack>
    </Backdrop>
  );
}
